/*
 * SNIP custom sections — interactions (theme-agnostic).
 * Integrates Add-to-Cart with the native Horizon cart:
 *   - POST /cart/add.js (real Shopify cart)
 *   - fly-to-cart animation toward the native cart icon
 *   - dispatch `cart:update` so the native cart icon + drawer refresh
 * Self-guards so duplicate script tags don't double-bind.
 */
(() => {
  'use strict';
  if (window.__snipSectionsInit) return;
  window.__snipSectionsInit = true;

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---- find the native cart icon (fly target) ---- */
  function cartTarget() {
    return document.querySelector(
      'cart-icon, .header-actions__cart-icon, .cart-bubble, ' +
      'a[href$="/cart"], a[href*="/cart"], [data-cart-toggle]'
    );
  }

  /* ---- fly-to-cart disc ---- */
  function flyToCart(originEl, color) {
    color = color || (originEl && originEl.getAttribute('data-accent')) || '#A8E63D';
    const target = cartTarget();
    if (!originEl || !target) return;
    const o = originEl.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const ox = o.left + o.width / 2, oy = o.top + o.height / 2;
    const tx = t.left + t.width / 2, ty = t.top + t.height / 2;
    const cx = (ox + tx) / 2, cy = Math.min(oy, ty) - 100;
    const disc = document.createElement('div');
    disc.style.cssText = 'position:fixed;left:' + (ox - 22) + 'px;top:' + (oy - 22) + 'px;width:44px;height:44px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff,' + color + ' 70%);box-shadow:0 6px 22px ' + color + '99;z-index:99999;pointer-events:none;display:flex;align-items:center;justify-content:center;font-family:Aileron,system-ui;font-weight:900;font-style:italic;font-size:13px;color:#0B0D14;letter-spacing:-0.04em;will-change:transform';
    disc.textContent = 'SNIP';
    document.body.appendChild(disc);
    const start = performance.now();
    const animate = (now) => {
      const p = Math.min(1, (now - start) / 800);
      const inv = 1 - p;
      const x = inv * inv * ox + 2 * inv * p * cx + p * p * tx;
      const y = inv * inv * oy + 2 * inv * p * cy + p * p * ty;
      disc.style.transform = 'translate(' + (x - ox) + 'px,' + (y - oy) + 'px) rotate(' + (p * 720) + 'deg) scale(' + (1 - p * 0.55) + ')';
      disc.style.opacity = p > 0.85 ? 1 - ((p - 0.85) / 0.15) : 1;
      if (p < 1) requestAnimationFrame(animate);
      else {
        disc.remove();
        const tEl = cartTarget();
        if (tEl) { tEl.classList.add('snip-pulse'); setTimeout(() => tEl.classList.remove('snip-pulse'), 500); }
      }
    };
    requestAnimationFrame(animate);
  }

  /* ---- add to Shopify cart + notify native theme ---- */
  async function cartAdd(variantId, qty) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }] })
      });
      if (!res.ok) { console.error('[snip] add failed', await res.text()); return null; }
      const cart = await (await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })).json();
      // Tell the native Horizon cart icon + drawer to refresh
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          data: { itemCount: cart.item_count, source: 'snip-section' }
        }
      }));
      return cart;
    } catch (e) { console.error('[snip]', e); return null; }
  }

  const BATTLE = ['#A8E63D', '#FF5C00', '#D8EDB5'];

  /* ---- delegated: quick-add buttons on cards / sections ---- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = btn.getAttribute('data-quick-add');
    if (!variantId || variantId === '') {
      // No variant wired yet — still play the animation for feedback
      flyToCart(btn, btn.getAttribute('data-accent'));
      return;
    }
    if (btn.hasAttribute('data-battle')) {
      BATTLE.forEach((c, i) => setTimeout(() => flyToCart(btn, c), i * 220));
    } else {
      flyToCart(btn, btn.getAttribute('data-accent'));
    }
    btn.disabled = true;
    await cartAdd(variantId, 1);
    setTimeout(() => { btn.disabled = false; }, 400);
  });

  /* ---- delegated: PDP-style form add ---- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pdp-add]');
    if (!btn) return;
    e.preventDefault();
    const form = btn.closest('form[data-product-form]');
    if (!form) return;
    const idEl = form.querySelector('[name="id"]');
    const qtyEl = form.querySelector('[name="quantity"]');
    const variantId = idEl && idEl.value;
    const qty = parseInt(qtyEl && qtyEl.value, 10) || 1;
    const handle = btn.getAttribute('data-product-handle') || '';
    if (/battle/i.test(handle)) BATTLE.forEach((c, i) => setTimeout(() => flyToCart(btn, c), i * 220));
    else flyToCart(btn, btn.getAttribute('data-accent'));
    btn.disabled = true;
    await cartAdd(variantId, qty);
    setTimeout(() => { btn.disabled = false; }, 400);
  });

  /* ---- PDP qty stepper (our sections) ---- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-input-change]');
    if (!btn) return;
    const delta = parseInt(btn.getAttribute('data-qty-input-change'), 10);
    const input = btn.parentElement.querySelector('[data-qty-input]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
  });

  /* ---- pulse keyframes ---- */
  const style = document.createElement('style');
  style.textContent = '.snip-pulse{animation:snip-cart-pulse 500ms cubic-bezier(0.34,1.56,0.64,1)}@keyframes snip-cart-pulse{0%{transform:scale(1)}35%{transform:scale(1.3)}60%{transform:scale(0.92)}100%{transform:scale(1)}}';
  document.head.appendChild(style);

  /* ---- Sticky "Ready to Flick?" CTA (appears on scroll) ---- */
  function initStickyFlick() {
    if (document.querySelector('.snip-sticky-flick')) return;
    // don't show on cart/checkout
    if (/\/(cart|checkout)/.test(location.pathname)) return;
    const shopUrl = (window.Shopify && '/collections/all') || '/collections/all';
    const bar = document.createElement('a');
    bar.className = 'snip-sticky-flick';
    bar.href = shopUrl;
    bar.innerHTML = '<span class="ssf-label">Ready to flick?</span><span class="ssf-cta">SHOP <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>';
    const css = document.createElement('style');
    css.textContent = `
      .snip-sticky-flick{position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom));transform:translateX(-50%) translateY(160%);z-index:9000;display:flex;align-items:center;gap:14px;padding:8px 8px 8px 22px;border-radius:999px;background:rgba(13,15,26,0.72);backdrop-filter:blur(20px) saturate(1.4);-webkit-backdrop-filter:blur(20px) saturate(1.4);border:1px solid rgba(255,255,255,0.14);box-shadow:0 16px 50px rgba(0,0,0,0.55),0 0 0 1px rgba(168,230,61,0.1),inset 0 1px 0 rgba(255,255,255,0.08);text-decoration:none;font-family:'CooperHewitt',system-ui,sans-serif;transition:transform .55s cubic-bezier(.34,1.4,.5,1),opacity .35s;opacity:0;width:max-content;max-width:calc(100vw - 24px)}
      .snip-sticky-flick.show{transform:translateX(-50%) translateY(0);opacity:1}
      .snip-sticky-flick .ssf-label{color:#fff;font-weight:600;font-size:15px;letter-spacing:-0.01em;white-space:nowrap}
      .snip-sticky-flick .ssf-cta{display:inline-flex;align-items:center;gap:6px;font-family:'Aileron',system-ui,sans-serif;font-style:italic;font-weight:900;font-size:14px;letter-spacing:0.02em;color:#0B0D14;background:#A8E63D;padding:11px 18px;border-radius:999px;white-space:nowrap;box-shadow:0 4px 16px rgba(168,230,61,0.4);transition:transform .18s}
      .snip-sticky-flick:active .ssf-cta{transform:scale(0.95)}
      .snip-sticky-flick:hover .ssf-cta{transform:scale(1.05)}
      @media(min-width:768px){.snip-sticky-flick .ssf-label{font-size:16px}.snip-sticky-flick .ssf-cta{font-size:15px;padding:12px 20px}}
    `;
    document.head.appendChild(css);
    document.body.appendChild(bar);
    let shown = false;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      const want = y > 620;
      if (want !== shown) { shown = want; bar.classList.toggle('show', want); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  if (document.readyState !== 'loading') initStickyFlick();
  else document.addEventListener('DOMContentLoaded', initStickyFlick);
})();

